import { Component, OnInit, OnDestroy, Renderer2, viewChild, ElementRef, Inject, HostListener, ChangeDetectorRef } from '@angular/core';
import { RouteInfo, ChildrenItems, SubChildrenItems, getSidebarRoutes } from '../sidebar/sidebar.routes';
import { Router, NavigationEnd } from '@angular/router';
import { DOCUMENT } from '@angular/common';
import { Subscription } from 'rxjs';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';

// Constantes a nivel de archivo
const MISC = {
    navbar_menu_visible: 0,
    active_collapse: true,
    disabled_collapse_init: 0,
    sidebar_mini_active: false,
    hide_sidebar_active: false,
};

const MOBILE_MENU_VISIBLE = 1;
const SIDEBAR_MINI_CLASS = 'sidebar-mini';
const NAV_OPEN_CLASS = 'nav-open';
const CLOSE_LAYER_CLASS = 'close-layer';
const VISIBLE_CLASS = 'visible';
const WINDOW_RESIZE_THRESHOLD = 991;

@Component({
    selector: 'app-navbar-cmp',
    templateUrl: 'navbar.component.html',
    styleUrls: ['navbar.component.scss'],
})
export class NavbarComponent implements OnInit, OnDestroy {
    private listTitles: RouteInfo[] = [];
    mobile_menu_visible = 0;
    private sidebarVisible = false;
    private routerSub: Subscription | null = null;
    private closeLayerClickUnlisten: (() => void) | null = null;

    /** Observable reactivo del título de la página actual */
    public readonly currentTitle$: Observable<string>;

    private readonly _titleSubject = new BehaviorSubject<string>('Inicio');

    // Migrated to signal-based queries
    readonly navbarRef = viewChild<ElementRef>('navbar');
    readonly navbarTogglerRef = viewChild<ElementRef>('navbarToggler');

    constructor(
        private renderer: Renderer2,
        private router: Router,
        @Inject(DOCUMENT) private document: Document,
        private cdr: ChangeDetectorRef
    ) {
        this.currentTitle$ = this._titleSubject.asObservable();
    }

    ngOnInit(): void {
        this.listTitles = [];
        this.initializeSidebarState();
        this.updateCurrentTitle();
        this.routerSub = this.router.events.pipe(
            filter(e => e instanceof NavigationEnd)
        ).subscribe(() => {
            this.updateCurrentTitle();
            this.cdr.markForCheck();
            this.sidebarClose();
            this.clearLayers();
        });

        void import('../../app.routing').then(({ APP_NAV_ROLES }) => {
            this.listTitles = getSidebarRoutes(APP_NAV_ROLES).filter(listTitle => listTitle);
            this.updateCurrentTitle();
            this.cdr.markForCheck();
        });
    }

    /**
     * Actualiza el título de la página actual basado en la ruta activa.
     * Compara la URL actual contra las rutas definidas en sidebar.routes.ts
     */
    private updateCurrentTitle(): void {
        const title = this.calculateTitle();
        this._titleSubject.next(title);
    }

    /**
     * Calcula el título correspondiente a la ruta actual.
     * Usa coincidencia exacta para evitar falsos positivos con sub-rutas.
     *
     * @returns El título de la página actual o 'Inicio' como fallback
     */
    private calculateTitle(): string {
        const url = this.sanitizeUrl(this.router.url);

        for (const route of this.listTitles) {
            const title = route.type === "link"
                ? this.findTitleForLink(url, route)
                : this.findTitleForSub(url, route);

            if (title) {
                return title;
            }
        }

        return this.getSessionTitle() || 'Inicio';
    }

    /**
     * Limpia la URL eliminando parámetros de query y fragmentos.
     */
    private sanitizeUrl(rawUrl: string): string {
        return rawUrl.split('?')[0].split('#')[0];
    }

    /**
     * Busca título en rutas de tipo link.
     */
    private findTitleForLink(url: string, route: RouteInfo): string | null {
        return this.isUrlMatch(url, route.path) ? route.title : null;
    }

    /**
     * Busca título en rutas de tipo sub (con hijos).
     */
    private findTitleForSub(url: string, route: RouteInfo): string | null {
        return this.findTitleInChildren(url, route.children || [], route.path);
    }

    /**
     * Verifica si la URL coincide con el path (coincidencia exacta o sub-ruta).
     */
    private isUrlMatch(url: string, path: string): boolean {
        return url === path || url.startsWith(path + '/');
    }

    /**
     * Construye el path completo concatenando path padre e hijo.
     */
    private buildFullPath(parentPath: string, childPath: string): string {
        return childPath.startsWith('/') ? childPath : `${parentPath}/${childPath}`;
    }

    /**
     * Busca título recursivamente en la lista de hijos.
     */
    private findTitleInChildren(url: string, children: ChildrenItems[], parentPath: string): string | null {
        for (const child of children) {
            // Hijo sin path: es un contenedor, buscar en sub-hijos
            if (!child.path) {
                const title = this.findTitleInSubChildren(url, child.children || [], parentPath);
                if (title) {
                    return title;
                }
                continue;
            }

            const childPath = this.buildFullPath(parentPath, child.path);

            // Verificar coincidencia con el hijo
            if (this.isUrlMatch(url, childPath)) {
                return child.title;
            }

            // Verificar en sub-hijos anidados
            const title = this.findTitleInSubChildren(url, child.children || [], childPath);
            if (title) {
                return title;
            }
        }

        return null;
    }

    /**
     * Busca título en sub-hijos (nivel anidado).
     */
    private findTitleInSubChildren(url: string, subChildren: SubChildrenItems[], basePath: string): string | null {
        for (const subChild of subChildren) {
            const subChildPath = this.buildFullPath(basePath, subChild.path);
            if (this.isUrlMatch(url, subChildPath)) {
                return subChild.title;
            }
        }
        return null;
    }

    /**
     * Obtiene el título desde sessionStorage.
     */
    private getSessionTitle(): string | null {
        return sessionStorage.getItem('title');
    }

    private initializeSidebarState(): void {
        const body = this.document.body;
        MISC.sidebar_mini_active = body.classList.contains(SIDEBAR_MINI_CLASS);
        MISC.hide_sidebar_active = body.classList.contains('hide-sidebar');
    }

    private clearLayers(): void {
        // Eliminar close-layer personalizado
        const layers = Array.from(this.document.querySelectorAll(`.${CLOSE_LAYER_CLASS}`));
        layers.forEach(layer => {
            if (layer.parentNode) {
                this.renderer.removeChild(layer.parentNode, layer);
            }
        });

        // Eliminar backdrops de Bootstrap 5 si existen
        const bootstrapBackdrops = Array.from(this.document.querySelectorAll('.modal-backdrop, .offcanvas-backdrop'));
        bootstrapBackdrops.forEach(backdrop => {
            if (backdrop.parentNode) {
                this.renderer.removeChild(backdrop.parentNode, backdrop);
            }
        });
    }

    @HostListener('window:resize', ['$event'])
    onResize(event?: Event): boolean {
        const w = window.innerWidth || this.document.documentElement.clientWidth;
        return w <= WINDOW_RESIZE_THRESHOLD;
    }

    sidebarOpen(): void {
        const body = this.document.body;
        const toggleEl: HTMLElement | null = this.navbarTogglerRef()?.nativeElement || body.querySelector('.navbar-toggler');

        if (toggleEl) {
            setTimeout(() => this.renderer.addClass(toggleEl, 'toggled'), 500);
        }

        this.renderer.addClass(body, NAV_OPEN_CLASS);
        this.createCloseLayer(body);
        this.mobile_menu_visible = MOBILE_MENU_VISIBLE;
        this.sidebarVisible = true;
    }

    private createCloseLayer(body: HTMLElement): void {
        const layer = this.renderer.createElement('div');
        this.renderer.setAttribute(layer, 'class', CLOSE_LAYER_CLASS);

        const mainPanel = this.document.querySelector('.main-panel') as HTMLElement | null;
        const appendTarget = mainPanel || (body.classList.contains('off-canvas-sidebar') ? this.document.querySelector('.wrapper-full-page') : body);
        if (appendTarget) {
            this.renderer.appendChild(appendTarget, layer);
        } else {
            this.renderer.appendChild(body, layer);
        }

        setTimeout(() => this.renderer.addClass(layer, VISIBLE_CLASS), 100);
        this.closeLayerClickUnlisten = this.renderer.listen(layer, 'click', () => this.sidebarClose());
    }

    sidebarClose(): void {
        const body = this.document.body;
        const togglerElem = body.querySelector('.navbar-toggler') as HTMLElement | null;
        if (togglerElem) {
            this.renderer.removeClass(togglerElem, 'toggled');
        }
        this.renderer.removeClass(body, NAV_OPEN_CLASS);

        // Eliminar clases de Bootstrap 5 que pueden bloquear el scroll
        this.renderer.removeClass(body, 'modal-open');
        this.renderer.removeStyle(body, 'overflow');
        this.renderer.removeStyle(body, 'padding-right');

        this.removeLayers();
        this.cleanupListeners();
        this.sidebarVisible = false;
        this.mobile_menu_visible = 0;
    }

    private removeLayers(): void {
        // Eliminar close-layer personalizado
        const layers = Array.from(this.document.querySelectorAll(`.${CLOSE_LAYER_CLASS}`));
        layers.forEach(layer => {
            if (layer.parentNode) {
                try {
                    this.renderer.removeChild(layer.parentNode, layer);
                } catch {
                    layer.parentNode?.removeChild(layer);
                }
            }
        });

        // Eliminar backdrops de Bootstrap 5 si existen
        const bootstrapBackdrops = Array.from(this.document.querySelectorAll('.modal-backdrop, .offcanvas-backdrop'));
        bootstrapBackdrops.forEach(backdrop => {
            if (backdrop.parentNode) {
                try {
                    this.renderer.removeChild(backdrop.parentNode, backdrop);
                } catch {
                    backdrop.parentNode?.removeChild(backdrop);
                }
            }
        });
    }

    private cleanupListeners(): void {
        if (this.closeLayerClickUnlisten) {
            this.closeLayerClickUnlisten();
            this.closeLayerClickUnlisten = null;
        }
    }

    sidebarToggle(): void {
        this.sidebarVisible ? this.sidebarClose() : this.sidebarOpen();
    }

    /**
     * Obtiene el título actual de la página.
     * Método mantenido por compatibilidad con el template original.
     *
     * @returns El título de la página actual
     * @deprecated Usar currentTitle$ con async pipe para reactividad
     */
    getTitle(): string {
        return this._titleSubject.value;
    }

    getPath(): string {
        return this.router.url; // Usar router.url para obtener la ruta actual
    }

    onLogOut(): void {
        this.router.navigate(['/auth/oauth-logout']);
    }

    changePassword(): void {
        this.router.navigate(['/users/update/password']);
    }

    minimizeSidebar(): void {
        const body = this.document.body;

        if (body.classList.contains(SIDEBAR_MINI_CLASS)) {
            this.renderer.removeClass(body, SIDEBAR_MINI_CLASS);
            MISC.sidebar_mini_active = false;
        } else {
            this.renderer.addClass(body, SIDEBAR_MINI_CLASS);
            MISC.sidebar_mini_active = true;
        }
    }

    ngOnDestroy(): void {
        if (this.routerSub) {
            this.routerSub.unsubscribe();
            this.routerSub = null;
        }
        this.cleanupListeners();
    }
}
